import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAtomValue } from 'jotai';
import { FC, useCallback } from 'react';
import { FieldPath, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { FormFactoryNoLayout } from '../../../../../components/Form/FormFactory';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';
import { FDMap } from '../../../../../util/data-structure/FDMap';
import { ICompanyViewModel } from '../../../../../view-models/company.view-model';
import { createForm, IForm } from '../../../../../view-models/form.view-model';
import { UnstyledFieldset } from '../../../../CompanyProfiles/Scenarios/components/commonStyledComponents';
import { FieldsWrapper, usePanelPagination } from '../../../../CompanyProfiles2/Summary/CompanyDetailsPanel';
import {
  fromCompanyDataModel,
  toCompanyDataModel,
} from '../../../../CompanyProfiles2/Summary/DigestPanel.view-model';
import { Panel } from '../../../../CompanyProfiles2/Summary/Panel';
import { useDebounceCompanyUpdates } from '../../../../CompanyProfiles2/Summary/useDebounceCompanyUpdates';
import { companyFormSchema, useCompanyFields } from '../../../../Finance2/Forms/CompanyFormUtils';
import { DealCompanyFieldsKeyType } from '../../../state/DealboardPreferences';
import { IDealPanelProps } from './DealAuditAndCustomDataPanel';
import { dealPanelFieldContainerCss } from './DealPanelStyles';

const fieldContainer = css`
  ${dealPanelFieldContainerCss};
  & [data-fieldkey='shortDescription'] {
    grid-column: 1/-1;
    @supports (field-sizing: content) {
      field-sizing: content;
    }
  }
`;

export const DealCompanyPanel: FC<IDealPanelProps> = ({ deal, preferences, editable = true }) => {
  const updateCompany = useDebounceCompanyUpdates(deal.companyId);
  const selectedFields = preferences.selectedFields;

  const company = useAtomValue(companyStateJ(deal.companyId));
  const schema = companyFormSchema();

  const methods = useForm<Partial<ICompanyViewModel>>({
    defaultValues: fromCompanyDataModel(company!) as Partial<ICompanyViewModel>,
    resolver: yupResolver(schema as ObjectSchema<Partial<ICompanyViewModel>>),
  });

  const onUpdateCompany = useCallback(
    async (data: Partial<ICompanyViewModel>) => {
      if (data.website) {
        try {
          await schema.validateAt('website', data);
        } catch (error) {
          return;
        }
      }
      updateCompany(toCompanyDataModel(data));
    },
    [schema, updateCompany]
  );

  return (
    <FormProvider {...methods}>
      <DealCompanyFields
        onChange={onUpdateCompany}
        editable={editable}
        selectedFields={selectedFields as DealCompanyFieldsKeyType[]}
      />
    </FormProvider>
  );
};

interface IDealCompanyFieldsProps {
  onChange: (data: Partial<ICompanyViewModel>) => Promise<void>;
  editable?: boolean;
  fieldsToShow?: FieldPath<ICompanyViewModel>[];
  defaultPage?: number;
  selectedFields: DealCompanyFieldsKeyType[];
}
function DealCompanyFields({ onChange, editable = true, selectedFields }: IDealCompanyFieldsProps) {
  const { formState } = useFormContext<Partial<ICompanyViewModel>>();
  const fields = useDealCompanyFields(selectedFields).map((f) => ({
    ...f,
    disabled:
      !editable ||
      f.key == '_viewModel.founderNames' ||
      (f.key === 'website' && formState?.defaultValues?.crunchbaseId != null),
  }));

  const { visibleFields, paginationConfig } = usePanelPagination({ fields, itemsPerPage: 8 });

  const form = createForm<ICompanyViewModel>({
    fields: FDMap.fromArray(visibleFields, 'key'),
    variant: 'form-inline',
  });

  return (
    <Panel title='Company Details' paginationConfig={paginationConfig}>
      <UnstyledFieldset disabled={!editable} css={fieldContainer}>
        <FieldsWrapper>
          <FormFactoryNoLayout form={form as unknown as IForm<ICompanyViewModel>} onChange={onChange} />
        </FieldsWrapper>
      </UnstyledFieldset>
    </Panel>
  );
}

export function useDealCompanyFields(selectedFields: DealCompanyFieldsKeyType[]) {
  return useCompanyFields({ fields: selectedFields, variant: 'form-inline', customFields: 'none' });
}
